import { Link, useNavigate } from '@tanstack/react-router';
import { Shield, EyeOff, UserCog, LogOut, FlaskConical } from 'lucide-react';
import { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAtom } from 'jotai';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { adminNavigation } from '@/components/navigation-sidebar/constants';
import { useUserProfile } from '@/contexts/user.context';
import { testModeAtom } from '@/store';

const TEST_MODE_FLAG = 'front-end-incomplete-functionality';

interface AdminMenuProps {
  onHide: () => void;
}

export function AdminMenu({ onHide }: AdminMenuProps) {
  const { user } = useUserProfile();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [testModeEnabled, setTestModeEnabled] = useAtom(testModeAtom);

  // Sync with PostHog flag on initial load. #6266: staging defaults test mode ON (the Meta App
  // Review environment) — the PostHog sync must not flip it back off there, only ever raise it.
  useEffect(() => {
    if (posthog) {
      const handleFlagsLoaded = () => {
        const flagValue = posthog.isFeatureEnabled(TEST_MODE_FLAG) ?? false;
        setTestModeEnabled(flagValue || import.meta.env.VITE_NODE_ENV === 'staging');
      };
      posthog.onFeatureFlags(handleFlagsLoaded);
    }
  }, [posthog, setTestModeEnabled]);

  const handleTestModeToggle = (checked: boolean) => {
    setTestModeEnabled(checked);
    if (posthog) {
      // Also persist to PostHog for cross-session consistency
      posthog.featureFlags.override({ [TEST_MODE_FLAG]: checked });
    }
  };

  const handleLogoutAsImpersonator = async () => {
    try {
      posthog?.capture('impersonation_ended');
      await navigate({
        to: '/impersonate',
        state: { impersonate: { action: 'logout' } },
      });
    } catch (error) {
      console.error('Failed to stop impersonating:', error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-700 hover:text-purple-800"
        >
          <Shield className="h-4 w-4" />
          <span className="sr-only">Admin Menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-56 rounded-lg"
        side="right"
        align="end"
        sideOffset={8}
      >
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to={'/impersonate' as any} className="cursor-pointer" onClick={() => posthog?.capture('user_impersonated')}>
              <UserCog className="mr-2 h-4 w-4" />
              <span>Impersonate User</span>
            </Link>
          </DropdownMenuItem>
          {adminNavigation.map((item) => (
            <DropdownMenuItem key={item.name} asChild>
              <Link to={item.to} className="cursor-pointer">
                <item.icon className="mr-2 h-4 w-4" />
                <span>{item.name}</span>
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>

        {user.impersonating_email && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogoutAsImpersonator} className="cursor-pointer">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Stop Impersonating</span>
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        {/* Test Mode Toggle */}
        <div className="flex items-center justify-between px-2 py-1.5">
          <div className="flex items-center">
            <FlaskConical className="mr-2 h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Test Mode</span>
          </div>
          <Switch
            checked={testModeEnabled}
            onCheckedChange={handleTestModeToggle}
            className="ml-2"
          />
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={onHide} className="cursor-pointer">
          <EyeOff className="mr-2 h-4 w-4" />
          <span>Hide Admin</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
